import {
  CapacityConflictAdjustVerb,
  CapacityConflictChoices,
  CapacityConflictGroupKind,
  CapacityConflictLine,
  CapacityConflictLineMode,
  CapacityConflictRowView,
} from '../types'

interface CapacityConflictGroup {
  kind: CapacityConflictGroupKind
  rows: CapacityConflictRowView[]
}

interface CapacityConflictAllocation {
  /** Nothing in the cart survives, so removal would empty the order. */
  allBlocked: boolean
  /** Lines the kitchen can't prepare as ordered. */
  blockingCount: number
  removeCount: number
  /** At least one pool is contested, so the guest picks where the capacity goes. */
  hasContestedPools: boolean
  verb: CapacityConflictAdjustVerb
  groups: CapacityConflictGroup[]
}

function modeOf(quantity: number, ceiling: number): CapacityConflictLineMode {
  if (quantity <= 0) {
    return CapacityConflictLineMode.Remove
  }

  return quantity >= ceiling
    ? CapacityConflictLineMode.Max
    : CapacityConflictLineMode.Reduce
}

/**
 * Works out what the kitchen can still make of every cart line, and how the guest's
 * choices spend what is left.
 *
 * Capacity belongs to the product, not the line: one pool per product, shared by that
 * product's cart lines. A pool more than one line draws on is contested, and those
 * lines go to the guest as one "choose what to keep" group; a line whose product is
 * sold out can't exist at all and is removed for them.
 *
 * Product add-ons are out of scope in this version - capacity is judged on the product
 * line alone, and no add-on is counted.
 */
export default function getCapacityConflictAllocation(
  lines: CapacityConflictLine[],
  capacities: Record<string, number>,
  choices: CapacityConflictChoices,
): CapacityConflictAllocation {
  const { lineModes, lineQuantities } = choices

  /** Units left in a pool. Only called with a key the map is known to hold. */
  const capacityOfKey = (key: string) => capacities[key]

  /**
   * The key a line's capacity is held under, or null when nothing caps it.
   *
   * The payload documents these as product ids. Matching the variant id too keeps
   * the join working if it ever carries those instead - a mismatch is otherwise
   * indistinguishable from "this product isn't capped", which shows up as an empty
   * modal rather than an error.
   */
  function poolKeyOf(line: CapacityConflictLine) {
    if (line.productId !== null && capacities[line.productId] !== undefined) {
      return line.productId
    }

    return capacities[line.variantId] !== undefined ? line.variantId : null
  }

  const poolKeyOfLine: Record<string, string | null> = {}

  /** Units left for this line, or null when nothing caps it. */
  function capacityOf(line: CapacityConflictLine) {
    const key = poolKeyOfLine[line.id]

    return key === null ? null : capacityOfKey(key)
  }

  /** The cart lines drawing on each capped pool. */
  const linesOf: Record<string, CapacityConflictLine[]> = {}

  lines.forEach(line => {
    const key = poolKeyOf(line)

    poolKeyOfLine[line.id] = key

    if (key === null) {
      return
    }

    linesOf[key] = linesOf[key] || []
    linesOf[key].push(line)
  })

  // Suggested split - each line takes what it can in cart order, except that a line
  // the guest has pinned takes only what they asked for, so whatever they freed flows
  // on to the next line that hasn't been touched.
  const remaining: Record<string, number> = {}

  Object.keys(linesOf).forEach(key => {
    remaining[key] = capacityOfKey(key)
  })

  const suggested: Record<string, number> = {}

  lines.forEach(line => {
    const key = poolKeyOfLine[line.id]
    const ceiling =
      key === null ? line.quantity : Math.min(line.quantity, remaining[key])
    const pinned = lineQuantities[line.id]
    const keep =
      pinned === undefined
        ? Math.max(0, ceiling)
        : Math.max(0, Math.min(pinned, ceiling))

    suggested[line.id] = keep

    if (key !== null) {
      remaining[key] -= keep
    }
  })

  // Current split - the suggested one, with the guest's overrides applied.
  function requestedQuantity(line: CapacityConflictLine) {
    const override = lineQuantities[line.id]
    const quantity = override === undefined ? suggested[line.id] || 0 : override

    return Math.max(0, Math.min(quantity, line.quantity))
  }

  /** What a pool has left once every line but `skip` has taken its share. */
  function roomFor(
    key: string,
    skip: CapacityConflictLine,
    keepOf: (line: CapacityConflictLine) => number,
  ) {
    const used = linesOf[key]
      .filter(line => line !== skip)
      .reduce((total, line) => total + keepOf(line), 0)

    return Math.max(0, capacityOfKey(key) - used)
  }

  /** A line can never exceed what its own pool has left for it. */
  function ceilingFor(
    line: CapacityConflictLine,
    keepOf: (line: CapacityConflictLine) => number,
  ) {
    const key = poolKeyOfLine[line.id]

    if (key === null) {
      return line.quantity
    }

    return Math.max(0, Math.min(line.quantity, roomFor(key, line, keepOf)))
  }

  const ceilingOf: Record<string, number> = {}
  const keptOf: Record<string, number> = {}

  lines.forEach(line => {
    ceilingOf[line.id] = ceilingFor(line, requestedQuantity)
    keptOf[line.id] = Math.min(requestedQuantity(line), ceilingOf[line.id])
  })

  const keepOf = (line: CapacityConflictLine) => keptOf[line.id]

  // Any pool the kitchen is short on is the guest's decision, whether two lines are
  // competing for it or one line simply asks for more than is left.
  const contested: Record<string, boolean> = {}

  Object.keys(linesOf).forEach(key => {
    const capacity = capacityOfKey(key)
    const demand = linesOf[key].reduce(
      (total, line) => total + line.quantity,
      0,
    )

    contested[key] = capacity > 0 && capacity < demand
  })

  /**
   * A line is beyond saving when its own product is sold out. Otherwise a line at
   * zero is only there because the guest spent the pool elsewhere, so it stays in
   * the pool group with its stepper.
   */
  const isHardBlocked = (line: CapacityConflictLine) => capacityOf(line) === 0

  function isPooled(line: CapacityConflictLine) {
    const key = poolKeyOfLine[line.id]

    return key !== null && contested[key] && !isHardBlocked(line)
  }

  const blocking = lines.filter(line => ceilingOf[line.id] < line.quantity)
  const allBlocked =
    lines.length > 0 && lines.every(line => ceilingOf[line.id] === 0)

  const pooledLines = lines.filter(isPooled)
  const toRemove = blocking.filter(
    line =>
      isHardBlocked(line) || (ceilingOf[line.id] === 0 && !isPooled(line)),
  )
  // A blocking line either still has capacity - which makes its pool contested, so
  // the guest decides - or has none at all and can't be made. Nothing sits between
  // the two, so there is no "trimmed for you" case to represent.
  const verb = pooledLines.length
    ? CapacityConflictAdjustVerb.Update
    : CapacityConflictAdjustVerb.Remove

  function rowOf(
    line: CapacityConflictLine,
    {
      adjustable,
      maxQuantity,
    }: {
      adjustable: boolean
      maxQuantity: number
    },
  ): CapacityConflictRowView {
    const kept = keptOf[line.id]

    return {
      lineId: line.id,
      name: line.name,
      variantType: line.variantType,
      orderedQuantity: line.quantity,
      keptQuantity: kept,
      maxQuantity,
      availableCount: capacityOf(line),
      // An untouched row defaults to keeping everything it can - never pre-removed,
      // never pre-reduced below its own available count.
      mode: adjustable
        ? lineModes[line.id] || CapacityConflictLineMode.Max
        : modeOf(kept, maxQuantity),
      isAdjustable: adjustable,
      blockedByOtherLines: null,
    }
  }

  const groups: CapacityConflictGroup[] = []

  // One "choose what to keep" group for every contested product: the guest sees all
  // the lines competing for capacity together. A row's stepper moves the line
  // quantity, which is what actually spends the pool.
  if (pooledLines.length) {
    groups.push({
      kind: CapacityConflictGroupKind.Choose,
      rows: pooledLines.map(line => {
        const key = poolKeyOfLine[line.id] as string
        const capacity = capacityOfKey(key)
        const used = linesOf[key].reduce(
          (total, item) => total + keepOf(item),
          0,
        )
        const left = capacity - used
        const kept = keptOf[line.id]
        const maxQuantity = Math.min(
          ceilingOf[line.id],
          kept + Math.max(0, left),
        )
        const row = rowOf(line, { adjustable: true, maxQuantity })

        return maxQuantity === 0
          ? {
              ...row,
              blockedByOtherLines: {
                productName: line.name,
                variantType: line.variantType,
                capacity,
              },
            }
          : row
      }),
    })
  }

  if (toRemove.length) {
    groups.push({
      kind: CapacityConflictGroupKind.Remove,
      rows: toRemove.map(line =>
        rowOf(line, { adjustable: false, maxQuantity: 0 }),
      ),
    })
  }

  return {
    allBlocked,
    blockingCount: blocking.length,
    removeCount: toRemove.length,
    hasContestedPools: pooledLines.length > 0,
    verb,
    groups,
  }
}
